import moment from 'moment';
import axios from 'common/utils/axios';
import React, { Fragment, useState } from 'react';
import {
  Typography,
  Skeleton,
  Divider,
  Stack,
  Box,
  Button,
  Grid,
} from 'common/components/material';
import useSession from 'common/hooks/useSession';
import useParamsProfileId from 'common/hooks/useParamsProfileId';
import useQuery from 'common/hooks/useQuery';
import useTheme from '@mui/material/styles/useTheme';
import { EllipsisText } from 'common/components/ellipsis-text/ellipsis-text.component';
import useIntersectionObserver from 'features/org-root/hooks/useIntersectionObserver';

const ITEMS_PER_CLICK = 3;
const matchingEngineApi = axios('matching-engine');

const useTrackRecord = (
  profileId: string,
  skillId?: string | string[],
  stepId?: string,
  cutOffDays?: number,
  disabled?: boolean,
) => {
  const { account: user, hasOrganization } = useSession();

  const getRecords = () => {
    const params = {
      profileId,
      skillId: typeof skillId === 'string' ? [skillId] : skillId,
      stepId,
      cutOffDays,
    };
    return matchingEngineApi.get(`track-record`, { params });
  };

  const queryKey = [
    'matching-engine',
    user?.organizationId,
    profileId,
    'track-record',
    skillId ?? stepId,
  ];

  const queryResponse = useQuery(queryKey, getRecords, {
    enabled: !!(hasOrganization && profileId) && !disabled,
  });

  return {
    ...queryResponse,
    data: queryResponse.data?.data,
  };
};

type Contribution = {
  id: string;
  date: Date;
  totalContributedDays: number;
  summary: string;
};

export type WorkItem = {
  id: string;
  date: Date;
  totalContributedDays: number;
  name: string;
  criticality: string;
  sponsor?: string;
  contributions: Contribution[];
};

export const RecentlyWorkedItem: React.FC<WorkItem> = (workItem) => {
  const { palette } = useTheme();

  return (
    <Stack>
      <Box sx={{ pt: 1.5 }}>
        <Stack flexDirection="column" lineHeight="2rem" sx={{ pr: 2 }}>
          <EllipsisText
            variant="body2"
            fontWeight="600"
            color={palette.Text.Headline}
            lineclamp={1}
          >
            {workItem.name}
          </EllipsisText>
          <Box lineHeight={1} sx={{ flex: '0 0 auto' }}>
            <Typography variant="body4" color={palette.Text.SubHeadline}>
              {sponsorLabel()}
            </Typography>
          </Box>
        </Stack>
      </Box>
      <>
        <Grid
          container
          justifyContent="space-between"
          sx={{ color: palette.Text.SubHeadline, py: 0.5 }}
        >
          <Grid item md={6}>
            <Typography variant="body3">Activity</Typography>
          </Grid>
          <Grid item md={3} sx={{ textAlign: 'left' }}>
            <Typography variant="body3">Effort(d)</Typography>
          </Grid>
          <Grid item md={3} sx={{ textAlign: 'right' }}>
            <Typography variant="body3">Latest</Typography>
          </Grid>
        </Grid>
        {[...workItem.contributions]
          .slice()
          .sort((a, b) => b.totalContributedDays - a.totalContributedDays)
          .map((contribution) => {
            function dateLabel() {
              return moment.utc(contribution.date).format('MMM YYYY');
            }
            const workDaysLabel = Math.ceil(contribution.totalContributedDays);

            return (
              <Grid
                container
                key={contribution.id}
                justifyContent="space-between"
                sx={{ py: 0.2 }}
              >
                <Grid item md={6}>
                  <Typography variant="body2" fontWeight={'500'}>
                    {contribution.summary}
                  </Typography>
                </Grid>
                <Grid item md={3} sx={{ textAlign: 'left' }}>
                  <Typography variant="body2" fontWeight={'500'}>
                    {workDaysLabel}
                  </Typography>
                </Grid>
                <Grid item md={3} sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" fontWeight={'500'}>
                    {dateLabel()}
                  </Typography>
                </Grid>
              </Grid>
            );
          })}
      </>
      <Divider
        sx={{ pt: '0.75rem', mt: '0.25rem', width: 'calc(100% - 0.5rem)' }}
        className="divider"
      />
    </Stack>
  );

  function sponsorLabel() {
    return (
      <Box py={1}>
        <Typography variant="body3">
          Sponsor:{' '}
          <Box component="span" fontWeight="600">
            {workItem.sponsor}
          </Box>
        </Typography>
      </Box>
    );
  }
};

const EndorsedSkillTrackRecord: React.FC<{
  profileId?: string;
  skillId?: string | string[];
  stepId?: string;
  cutOffDays?: number;
}> = ({ profileId, skillId, stepId, cutOffDays }) => {
  const { palette } = useTheme();
  const { profileId: profileId2 } = useParamsProfileId();
  const { setNode, intersectionObserverEntry } = useIntersectionObserver();
  profileId = profileId ?? profileId2;
  const { data, isLoading } = useTrackRecord(
    profileId,
    skillId,
    stepId,
    cutOffDays,
    !intersectionObserverEntry?.isIntersecting,
  );

  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_CLICK);

  const itemsToShow = data?.items?.slice(0, visibleCount) ?? [];

  const handleShowMore = () => {
    setVisibleCount((prev) => prev + ITEMS_PER_CLICK);
  };

  if (isLoading || !data) {
    return (
      <Skeleton
        ref={setNode}
        variant="rectangular"
        sx={{ borderRadius: '0.75rem', mr: 2, mb: 3 }}
        height="6rem"
      />
    );
  }
  return (
    <Typography variant="body3" color={palette.Text.Headline}>
      {data?.items.length === 0 && (
        <Typography variant="body4" sx={{ fontStyle: 'italic' }}>
          Information not found
        </Typography>
      )}

      {itemsToShow.map((item: any, index: number) =>
        item.type === 'WORK_ITEM' ? (
          <Box key={item.id} sx={{ mb: 3 }}>
            <RecentlyWorkedItem {...item} />
          </Box>
        ) : null,
      )}

      {visibleCount < (data?.items?.length ?? 0) && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
          <Button size="small" onClick={handleShowMore}>
            Show More
          </Button>
        </Box>
      )}
    </Typography>
  );
};

export default EndorsedSkillTrackRecord;
